// The setup list over home (onboarding variant B): setting up is itself a
// short honey-do list, each item paying a few welcome points. On a phone it
// sits above the list; on a desk at the top of the rail. Take the tour; sign
// in, and save a passkey; add the app to the Home Screen, with this browser's
// steps; hear when a plan is ready (offered only inside the installed app,
// from a tap); start a first project, with the credit in view. Every item
// can be skipped, the list can be put away, and once every item is done or
// skipped it goes with a small celebration. Rules in setup.ts; the state
// on the device in setupStore.ts.
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { AccountOverview } from '@shared/account';
import { DEMO_PROJECTS } from '@shared/demo';
import { api, failureMessage } from '@app/api';
import { useMe } from '@app/auth/me';
import { installPrompt } from '@app/pwa';
import { useNav } from '@app/router';
import { requireAccount } from '@app/session';
import { isIOS } from '@app/shell/hintVariant';
import type * as Push from '@app/push';
import { useProjects } from '@app/store/hooks';
import { Button, Card, Icon, TextButton, confetti, countUp, cx, pop, spark } from '@app/ui';
import { detectBrowser, type Browser } from './browser';
import type * as HomeScreen from './homeScreen';
import {
  PARENT, SETUP_POINTS, celebrate, creditLine, dismiss, items, mark, observe, phase, status, tally, unskip, welcomePoints,
  type SetupFacts, type SetupItem,
} from './setup';
import { setupStore, useSetup } from './setupStore';
import './setup.css';

type PushModule = typeof Push;

// The Home Screen steps for every browser are a table the shell need not
// carry: fetched the first time the list shows, and kept.
let homeScreen: typeof HomeScreen | null = null;
let homeScreenLoading: Promise<typeof HomeScreen> | null = null;
function useHomeScreen(): typeof HomeScreen | null {
  const [mod, setMod] = useState(homeScreen);
  useEffect(() => {
    if (mod) return;
    let alive = true;
    homeScreenLoading ??= import('./homeScreen').then((m) => (homeScreen = m));
    void homeScreenLoading.then((m) => { if (alive) setMod(m); });
    return () => { alive = false; };
  }, [mod]);
  return mod;
}

const DEMO_IDS = new Set(DEMO_PROJECTS.map((p) => p.id));
const CELEBRATE_MS = 2600;

// ---------- what the device can tell

let installedHere = false;
const standaloneNow = (): boolean =>
  installedHere || matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
function subscribeStandalone(onChange: () => void): () => void {
  const mq = matchMedia('(display-mode: standalone)');
  const installed = () => { installedHere = true; onChange(); };
  mq.addEventListener('change', onChange);
  addEventListener('appinstalled', installed);
  return () => { mq.removeEventListener('change', onChange); removeEventListener('appinstalled', installed); };
}

const env = () => ({ ua: navigator.userAgent, platform: navigator.platform, maxTouchPoints: navigator.maxTouchPoints });
let browserHere: Browser | undefined;
const passkeysHere = (): boolean => typeof window.PublicKeyCredential === 'function';

/** Notifications as far as the shell can tell without the push module: whether they can be offered here at all. */
function notifyHere(standalone: boolean): SetupFacts['notify'] {
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (isIOS(env()) && !standalone && !supported) return 'install-first';
  if (!supported) return 'unsupported';
  return Notification.permission === 'denied' ? 'blocked' : 'off';
}

/** Whether the account has a passkey, asked once per person while the item is open. */
const passkeyAnswers = new Map<string, Promise<boolean>>();
function usePasskey(userId: string | null, wanted: boolean): boolean {
  const [has, setHas] = useState<string | null>(null);
  useEffect(() => {
    if (!userId || !wanted) return;
    let alive = true;
    let asked = passkeyAnswers.get(userId);
    if (!asked) {
      asked = api<AccountOverview>('/api/account').then((o) => o.passkeys > 0, () => false);
      passkeyAnswers.set(userId, asked);
    }
    void asked.then((yes) => { if (alive && yes) setHas(userId); });
    return () => { alive = false; };
  }, [userId, wanted]);
  return has !== null && has === userId;
}

/** The push module, loaded only where notifications can be offered (installed, signed in), so the tap that offers is synchronous. */
function usePush(wanted: boolean): { mod: PushModule | null; state: SetupFacts['notify'] | null } {
  const [mod, setMod] = useState<PushModule | null>(null);
  const [state, setState] = useState<SetupFacts['notify'] | null>(null);
  useEffect(() => {
    if (!wanted) return;
    let alive = true;
    let off = () => {};
    void import('@app/push').then((m) => {
      if (!alive) return;
      setMod(m);
      const read = () => setState(m.notify.get());
      off = m.notify.subscribe(read);
      void m.notify.refresh().then(read);
    });
    return () => { alive = false; off(); };
  }, [wanted]);
  return { mod, state };
}

// ---------- the words

function homeScreenTitle(browser: Browser): string {
  if (browser === 'mac-safari') return 'Add it to your Dock';
  if (browser.startsWith('desktop') || browser === 'other') return 'Install it as an app';
  return 'Add it to your Home Screen';
}

const phone = (browser: Browser) => browser.startsWith('ios') || browser.startsWith('android');

const TITLES: Record<Exclude<SetupItem, 'homescreen'>, string> = {
  tour: 'Take the tour',
  signin: 'Sign in with a code',
  passkey: 'Save a passkey',
  notify: 'Hear when a plan is ready',
  project: 'Start your first project',
};

export function SetupCard() {
  const nav = useNav();
  const state = useSetup();
  const { me } = useMe();
  const account = me && me !== 'loading' ? me : null;
  const projects = useProjects();
  const standalone = useSyncExternalStore(subscribeStandalone, standaloneNow, () => false);
  const canPrompt = useSyncExternalStore(installPrompt.subscribe, () => installPrompt.get() !== null, () => false);
  const hs = useHomeScreen();
  const browser = (browserHere ??= detectBrowser(env()));
  const steps = hs?.HOME_SCREEN_STEPS[browser];

  const hasPasskey = usePasskey(account?.user.id ?? null, status(state, 'passkey') === 'open');
  const push = usePush(standalone && !!account && status(state, 'notify') === 'open');
  const facts: SetupFacts = {
    signedIn: !!account,
    passkey: passkeysHere() ? hasPasskey : 'unsupported',
    standalone,
    notify: push.state ?? notifyHere(standalone),
    ownProject: projects.some((p) => !DEMO_IDS.has(p.id)),
  };
  // What the world has done, marked as done (and paid) as soon as the device can tell.
  const factsKey = JSON.stringify(facts);
  useEffect(() => { setupStore.update((s) => observe(s, JSON.parse(factsKey) as SetupFacts)); }, [factsKey]);

  const [open, setOpen] = useState<SetupItem | null>(null);
  const [said, setSaid] = useState<Partial<Record<SetupItem, string>>>({});
  const [busy, setBusy] = useState<SetupItem | null>(null);
  const say = (item: SetupItem, words: string | undefined) => setSaid((s) => ({ ...s, [item]: words }));

  const now = phase(state, facts);
  const pts = welcomePoints(state);
  const { done, of } = tally(state, facts);
  const ptsEl = useRef<HTMLSpanElement>(null);
  const shownPts = useRef(pts);

  // A newly done item sparks where it is, and the points count up, wherever it was done.
  useLayoutEffect(() => {
    const from = shownPts.current;
    shownPts.current = pts;
    if (from !== pts && ptsEl.current) { countUp(ptsEl.current, from, pts, 450); pop(ptsEl.current); }
  }, [pts]);
  useEffect(() => {
    if (now === 'away') return;
    for (const item of setupStore.takeFresh()) {
      const box = document.querySelector<HTMLElement>(`.setup [data-setup="${item}"] .setup-box`);
      if (!box) continue;
      const r = box.getBoundingClientRect();
      spark(r.left + r.width / 2, r.top + r.height / 2);
      pop(box);
    }
  });

  // Settled: a small celebration, and then the list goes for good.
  useEffect(() => {
    if (now !== 'celebrate') return;
    confetti();
    const t = setTimeout(() => setupStore.update(celebrate), CELEBRATE_MS);
    return () => clearTimeout(t);
  }, [now]);

  if (now === 'away') return null;
  if (now === 'celebrate') {
    return (
      <Card as="section" className="setup setup-done" role="status" aria-label="Set up Honey-Do">
        <img src="/art/honeypot.svg" alt="" width={48} height={48} />
        <p><b>You’re all set.</b> <span className="num">{pts}</span> welcome points, banked.</p>
      </Card>
    );
  }

  // ---------- each item: its words and what a tap does

  const signIn = () => { void requireAccount().catch(() => undefined); };
  // The router matches paths only, so the beat goes on the URL after the push: just the tour, not the replay.
  const takeTour = () => {
    nav.push('/welcome');
    history.replaceState(history.state, '', '/welcome?beat=home');
  };
  const savePasskey = async () => {
    setBusy('passkey');
    say('passkey', undefined);
    const { passkeyMessage, savePasskey: save } = await import('@app/auth/passkey');
    try {
      await save();
      if (account) passkeyAnswers.set(account.user.id, Promise.resolve(true));
      setupStore.update((s) => mark(s, 'passkey', 'done'));
    } catch (err) {
      say('passkey', passkeyMessage(err, 'save'));
    } finally {
      setBusy(null);
    }
  };
  const offerNotify = () => {
    const m = push.mod;
    if (!m) return;
    say('notify', undefined);
    // From the tap, with nothing awaited first: iOS grants it only so.
    void m.offerNotifications().then((r) => {
      if (r.outcome === 'on') setupStore.update((s) => mark(s, 'notify', 'done'));
      else if (r.outcome === 'blocked') say('notify', 'Notifications are off for Honey-Do in this device’s settings.');
      else if (r.outcome === 'declined') say('notify', 'No notification for now. You can turn it on here any time.');
      else say('notify', r.message ?? 'Notifications can’t be turned on here.');
    }, (err: unknown) => say('notify', failureMessage(err)));
  };
  const install = async () => {
    if ((await installPrompt.show()) === 'accepted') setupStore.update((s) => mark(s, 'homescreen', 'done'));
  };

  const where = phone(browser) ? 'the app on your Home Screen' : browser === 'mac-safari' ? 'the app in your Dock' : 'the installed app';
  const notifyWords = (): { sub: string; act?: () => void } => {
    if (facts.notify === 'on') return { sub: 'We only say when a plan is ready.' };
    if (facts.notify === 'blocked') return { sub: 'Notifications are off for Honey-Do in this device’s settings.' };
    if (!standalone) return { sub: `Waits for ${where}. Turn it on from there.` };
    if (!account) return { sub: 'Sign in first, so we know where to send it.', act: signIn };
    return { sub: 'One tap. We only say when a plan is ready.', act: push.mod ? offerNotify : undefined };
  };

  type Spec = { title: string; sub: string; act?: () => void; expands?: boolean };
  const spec = (item: SetupItem): Spec => {
    switch (item) {
      case 'tour': return { title: TITLES.tour, sub: 'A demo project, from photo to plan, in a minute.', act: takeTour };
      case 'signin': return { title: TITLES.signin, sub: account ? account.user.email : 'We email you a code. No password, ever.', act: account ? undefined : signIn };
      case 'passkey': return account
        ? { title: TITLES.passkey, sub: 'Face ID or a fingerprint next time, instead of a code.', act: busy === 'passkey' ? undefined : () => void savePasskey() }
        : { title: TITLES.passkey, sub: 'Once you’re signed in.' };
      case 'homescreen': return {
        // Tappable before the steps arrive; they fill in when they land. The space keeps the line's height meanwhile.
        title: homeScreenTitle(browser), sub: standalone ? 'Opened from there. That’s the one.' : steps?.summary ?? '\u00a0',
        act: standalone ? undefined : () => setOpen((o) => (o === 'homescreen' ? null : 'homescreen')), expands: !standalone,
      };
      case 'notify': return { title: TITLES.notify, ...notifyWords() };
      case 'project': {
        const line = account ? creditLine(account.balanceCents, account.estimateCents.plan) : undefined;
        return { title: TITLES.project, sub: line ?? 'Snap the spot and say what should change.', act: () => nav.push('/new') };
      }
    }
  };

  return (
    <Card as="section" className="setup" aria-labelledby="setup-title">
      <div className="setup-head">
        <div className="setup-heading">
          <h2 id="setup-title">Set up Honey-Do</h2>
          <span className="setup-tally">
            {done} of {of} done · <span className="num" ref={ptsEl}>{pts}</span> welcome points
          </span>
        </div>
        <TextButton className="setup-later" onClick={() => setupStore.update(dismiss)}>Not now</TextButton>
      </div>
      <ul className="setup-list">
        {items(state, facts).map((item) => {
          const st = status(state, item);
          const s = spec(item);
          const act = st === 'done' ? undefined : s.act;
          const expanded = open === item && st !== 'done';
          const inner = (
            <>
              <span className="setup-box" aria-hidden="true"><Icon name="check" /></span>
              <span className="setup-text">
                <b>{s.title}</b>
                <small>{s.sub}</small>
                {st !== 'open' && <span className="sr">, {st}</span>}
              </span>
              <span className="setup-pts num" aria-label={`${SETUP_POINTS[item]} points`}>+{SETUP_POINTS[item]}</span>
            </>
          );
          return (
            <li key={item} className={cx('setup-item', st, PARENT[item] && 'sub')} data-setup={item}>
              <div className="setup-row">
                {act
                  ? <button type="button" className="setup-main" onClick={act} aria-expanded={s.expands ? expanded : undefined}>{inner}</button>
                  : <div className="setup-main">{inner}</div>}
                {st === 'open' && <TextButton className="setup-skip" onClick={() => setupStore.update((x) => mark(x, item, 'skipped'))} aria-label={`Skip: ${s.title}`}>Skip</TextButton>}
                {st === 'skipped' && <TextButton className="setup-skip" onClick={() => setupStore.update((x) => unskip(x, item))} aria-label={`Undo skipping: ${s.title}`}>Undo</TextButton>}
              </div>
              {expanded && item === 'homescreen' && steps && (
                <div className="setup-more">
                  <ol className="setup-steps">{steps.steps.map((line) => <li key={line}>{line}</li>)}</ol>
                  {canPrompt && <Button variant="honey" size="small" icon="download" onClick={() => void install()}>Install</Button>}
                </div>
              )}
              {said[item] && <p className="setup-said" role="status">{said[item]}</p>}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
